/**
 * 自分の投稿一覧 (/my-reports)
 *
 * localStorage に保存された viewer_token 一覧を読み出して、過去の投稿を一覧表示する.
 * **完全クライアントローカル** — サーバ問い合わせなしで動く (匿名性維持).
 * 各エントリの URL (/my-report/{token}) から個別の状況確認ページへ.
 *
 * 注意: localStorage は端末・ブラウザ固有.
 *  - 別端末からはアクセス不可
 *  - シークレットモード / Cookie クリアで消える
 *  - 同じ端末でも 50 件超過分は古いほうから削除
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Inbox, Trash2, AlertTriangle, Clock } from 'lucide-react';
import type { MyReportRecord } from '../types';

const MY_REPORTS_LS_KEY = 'genbasafeai_my_reports';

function loadMyReports(): MyReportRecord[] {
  try {
    const raw = localStorage.getItem(MY_REPORTS_LS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.filter((r): r is MyReportRecord =>
      r && typeof r.token === 'string' && typeof r.caseId === 'string'
    );
  } catch {
    return [];
  }
}

function clearMyReports() {
  try {
    localStorage.removeItem(MY_REPORTS_LS_KEY);
  } catch {
    // ignore
  }
}

const formatRelativeJST = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    const now = Date.now();
    const diffMs = now - d.getTime();
    const min = Math.floor(diffMs / 60000);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);
    if (day >= 1) return `${day}日前`;
    if (hr >= 1) return `${hr}時間前`;
    if (min >= 1) return `${min}分前`;
    return 'たった今';
  } catch {
    return '-';
  }
};

const formatDateJST = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

interface MyReportsListViewProps {
  onBack: () => void;
  /** リスト内の各エントリをタップしたとき、個別の MyReportView ('/my-report/{token}') へ */
  onOpenReport: (token: string) => void;
}

export const MyReportsListView: React.FC<MyReportsListViewProps> = ({ onBack, onOpenReport }) => {
  const [reports, setReports] = useState<MyReportRecord[]>([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    setReports(loadMyReports());
  }, []);

  const handleClear = () => {
    clearMyReports();
    setReports([]);
    setShowClearConfirm(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-industrial-900 text-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-slate-200 hover:text-white"
          >
            <ArrowLeft size={16} />
            戻る
          </button>
          <div className="flex-1 text-center flex items-center justify-center gap-2">
            <img src="/icon-512x512.png" alt="" className="h-5 w-5 rounded" />
            <span className="font-bold text-sm">安全ポスト＋</span>
          </div>
          <span className="w-12" aria-hidden />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-lg font-bold text-industrial-900 mb-1">これまでの投稿</h1>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          この端末から投稿した報告の一覧です (最新 50 件まで)。
          <br />
          別端末では表示されません。ブラウザのデータを消すと履歴も消えます。
        </p>

        {reports.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <Inbox className="mx-auto mb-3 text-slate-300" size={36} />
            <p className="text-sm font-medium text-slate-700 mb-1">まだ投稿がありません</p>
            <p className="text-xs text-slate-500">
              QRコードから報告すると、ここに履歴が残ります。
            </p>
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {reports.map((r) => (
                <li key={r.token}>
                  <button
                    onClick={() => onOpenReport(r.token)}
                    className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 active:bg-slate-50 transition shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-industrial-900 leading-snug mb-1.5 line-clamp-2">
                          {r.title || '(無題の報告)'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock size={12} />
                          <span>{formatRelativeJST(r.createdAt)}</span>
                          <span className="text-slate-300">・</span>
                          <span>{formatDateJST(r.createdAt)}</span>
                        </div>
                      </div>
                      <ArrowRight size={16} className="text-slate-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="leading-relaxed">
                ここの履歴はこの端末のブラウザにだけ保存されています。端末や履歴を消すと復元できません。
                重要な確認 URL は別途メモすることをおすすめします。
              </p>
            </div>

            {!showClearConfirm ? (
              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-600 transition"
              >
                <Trash2 size={12} />
                履歴をすべて消す
              </button>
            ) : (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-xs flex items-center justify-between gap-2">
                <span className="text-red-800">本当に消しますか? (元に戻せません)</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="px-3 py-1.5 bg-white text-slate-700 border border-slate-300 rounded-md font-medium"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={handleClear}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-md font-bold"
                  >
                    消す
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};
